import {useEffect, useMemo, useState} from 'react'
import type {PropsWithChildren, JSX} from 'react'
import App from 'next/app'
import type {AppContext, AppProps} from 'next/app'
import {useReportWebVitals} from 'next/web-vitals'
import {ReactQueryDevtools} from 'react-query/devtools'
import {QueryClient, QueryClientProvider} from 'react-query'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import '../styles/globals.css'
import {GoogleTagManager} from '@next/third-parties/google'
import findLastIndex from 'array.prototype.findlastindex'
import {SmartLinkProvider} from '@/services/kontent-ai/preview-mode/smart-link'
import type {PageProps} from '@/pages/[[...slug]]'
import {env} from '@/services/env/client'
import {useMikMak} from '@/services/mikmak'
import {AuthProvider} from '@/products/auth/auth-provider'
import {PageSEO} from '@/products/flexible-web-toolkit/components/page-seo'
// Needed for rich-text-renderer
import {useTrustArcConsent} from '@/services/trustarc'
import {disablePageView, enablePageView} from '@/services/logger/client'
import {useBazaarVoice} from '@/services/bazaar-voice'
import {PageContextProvider} from '@/lib/pageContext/page-context-provider'
import {
	isSiteCode,
	type PageContext,
} from '@/lib/pageContext/page-context-store'
import {reportWebVitalsMetric} from '@/lib/telemetry/web-vitals'
import {PearCommerceCoordinator} from '@/components/third-party-scripts/pear-commerce-coordinator'
import {MikMakCoordinator} from '@/components/third-party-scripts/mikmak-coordinator'
import {BazaarVoiceCoordinator} from '@/components/third-party-scripts/bazaarvoice-coordinator'

findLastIndex.shim()

const BackToTop = dynamic(
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error -- Necessary to fix build
	// @ts-ignore -- Necessary to fix build
	() =>
		import('@/products/flexible-web-toolkit/components/back-to-top-button').then(
			(mod) => mod.BackToTopButton
		),
	{
		ssr: false,
	}
)

const Layout = (props: PropsWithChildren<PageProps>): JSX.Element => {
	const router = useRouter()

	if (router.locale === 'global') {
		return (
			<>
				<div
					className={clsx(
						props.isInPreviewMode ? 'preview-mode' : null,
						'pt-6'
					)}
				/>
				{props.children}
				<BackToTop
					altText={
						props.page.elements.snippetPageSeoBackToTopButtonAltText
					}
					hide={
						props.page.elements.snippetPageSeoHideBackToTopButton[0]
							?.codename === 'hide'
					}
				/>
			</>
		)
	}
	return (
		<div className="flex min-h-screen flex-col">
			{props.children}
			<BackToTop
				altText={
					props.page.elements.snippetPageSeoBackToTopButtonAltText
				}
				hide={
					props.page.elements.snippetPageSeoHideBackToTopButton[0]
						?.codename === 'hide'
				}
			/>
		</div>
	)
}

const KontentApplication = ({
	Component,
	pageProps: rawPageProps,
	router,
}: AppProps<PageProps>): JSX.Element => {
	useReportWebVitals(reportWebVitalsMetric)

	const pageProps = rawPageProps
	const contextValue: PageContext = useMemo(
		() => ({
			template: pageProps.template || 'unknown',
			site: isSiteCode(pageProps.site) ? pageProps.site : 'PETP',
			locale: pageProps.locale || 'unknown',
		}),
		[pageProps.locale, pageProps.site, pageProps.template]
	)

	const gtmContainerID = (
		pageProps.globalConfig.elements.gtmContainerId || ''
	).trim()
	const trustBanner = (
		pageProps.globalConfig.elements.trustArcBanner || ''
	).trim()
	const country = (pageProps.globalConfig.elements.country || 'us').trim()
	const language = (pageProps.globalConfig.elements.language || 'en').trim()
	const mikMakAppId = pageProps.globalConfig.elements.appId || null
	const pearCommerceId =
		pageProps.globalConfig.elements.pearCommerceId || null
	const bazaarVoiceLocale =
		env.NEXT_PUBLIC_TOWER === 'pet' && country === 'us' ? 'en_US' : null
	const environment =
		env.NEXT_PUBLIC_NODE_ENV === 'production'
			? ('production' as const)
			: ('staging' as const)
	useMikMak(mikMakAppId)
	useBazaarVoice(router.locale)

	useEffect(() => {
		if (router.locale === 'global') return
		const taScript = document.createElement('script')
		taScript.src = `https://consent.trustarc.com/notice?domain=${trustBanner}&c=teconsent&js=nj&noticeType=bb&text=true&country=${country}&language=${language}&gtm=1`
		document.head.appendChild(taScript)
		return () => {
			taScript.remove()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only run on locale change
	}, [router.locale])

	useEffect(() => {
		// Function to update lang attribute on the <html> tag during page load and on route change
		const updateLang = (): void => {
			const langHTMLTag = pageProps.globalConfig.elements.langHtmlTag
			if (langHTMLTag && document.documentElement.lang !== langHTMLTag) {
				document.documentElement.setAttribute('lang', langHTMLTag)
			}
		}
		const handleRouteChange = (): void => {
			updateLang()
		}
		router.events.on('routeChangeComplete', handleRouteChange)

		return () => {
			router.events.off('routeChangeComplete', handleRouteChange)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- removing dependency to run only once
	}, [])

	// Create a query client
	// eslint-disable-next-line react/hook-use-state -- React query initialization
	const [queryClient] = useState(() => new QueryClient())

	const cookieConsent = useTrustArcConsent()

	useEffect(() => {
		if (
			cookieConsent === 'ALL_COOKIES' ||
			cookieConsent === 'FUNCTIONAL_COOKIES'
		) {
			enablePageView()
		} else {
			disablePageView()
		}
	}, [cookieConsent])

	if (router.locale === 'null') return <p>404 - Not found</p>

	return (
		<>
			{gtmContainerID && router.locale !== 'global' ? (
				<GoogleTagManager gtmId={gtmContainerID} />
			) : null}
			<PearCommerceCoordinator pearCommerceId={pearCommerceId} />
			<MikMakCoordinator mikMakAppId={mikMakAppId} />
			<BazaarVoiceCoordinator
				bazaarVoiceLocale={bazaarVoiceLocale}
				environment={environment}
			/>
			{/* React code can read page context via usePageContext, while telemetry reads the synced module store. */}
			<PageContextProvider value={contextValue}>
				<SmartLinkProvider>
					<QueryClientProvider client={queryClient}>
						{/* PageSEO sits outside AuthProvider so meta tags reach SSR for unauthenticated requests (GWP-5938) */}
						<PageSEO
							globalConfig={pageProps.globalConfig}
							isOnTaxonomyTermPage={
								pageProps.isOnTaxonomyTermPage
							}
							page={pageProps.page}
							reqHost={pageProps.reqHost}
							reqUrl={pageProps.reqUrl}
						/>
						<AuthProvider {...pageProps}>
							<Layout {...pageProps}>
								<Component {...pageProps} />
							</Layout>
						</AuthProvider>
						<ReactQueryDevtools initialIsOpen={false} />
					</QueryClientProvider>
				</SmartLinkProvider>
			</PageContextProvider>
		</>
	)
}

/** Wrapper that routes non-Kontent pages (e.g. /colors) without Kontent providers */
const Application = (props: AppProps<PageProps>): JSX.Element => {
	// Non-Kontent pages don't have globalConfig - render them without Kontent wrappers
	if (props.router.pathname === '/colors') {
		return <props.Component {...props.pageProps} />
	}
	return <KontentApplication {...props} />
}

Application.getInitialProps = async (appContext: AppContext) => {
	const appProps = await App.getInitialProps(appContext)
	if (appContext.router.locale === 'null') return {notFound: true}
	return {...appProps}
}

export default Application
